'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["slots", "slotProps", "className", "sx", "children", "messageId", "classes"],
  _excluded2 = ["groupKey", "index", "items", "messageId"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { MessageGroup } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { mergeSlotProps } from "../internals/mergeSlotProps.mjs";
import { useChatMessageUtilityClasses } from "./chatMessageClasses.mjs";
import { ChatMessage } from "./ChatMessage.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatMessageGroup');

/**
 * Flat slot keys for `ChatMessageGroup` — part of the message-rendering pipeline
 * vocabulary. `messageGroup` is the styled group wrapper (wrapper-only); the
 * `message*` keys are mapped onto the inner `ChatMessage`'s short local slots.
 * A subset of the public `ChatBoxSlots`.
 */

const ChatMessageGroupStyled = styled('div', {
  name: 'MuiChatMessage',
  slot: 'Group',
  overridesResolver: (_, styles) => styles.group
})(({
  theme,
  ownerState
}) => {
  const densityMarginBlockStart = {
    compact: theme.spacing(0.25),
    standard: theme.spacing(1),
    comfortable: theme.spacing(2)
  };
  const marginBlockStart = ownerState?.isFirst && !ownerState?.isFirstInList ? densityMarginBlockStart[ownerState?.density ?? 'standard'] : 0;
  return {
    '--MuiChatMessage-avatarSize': ownerState?.variant === 'compact' ? '28px' : '36px',
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    width: '100%',
    marginBlockStart,
    // When the avatar slot is hidden, drop the reserved avatar size so any
    // descendant relying on the variable (padding, author-name offset) collapses too.
    '&.MuiChatMessage-noAvatar': {
      '--MuiChatMessage-avatarSize': '0px'
    }
  };
});
const ChatMessageGroupAuthorNameStyled = styled('div', {
  name: 'MuiChatMessage',
  slot: 'GroupAuthorName'
})(({
  theme,
  ownerState
}) => _extends({
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  color: (theme.vars || theme).palette.text.secondary,
  marginBottom: 0
}, ownerState?.variant === 'compact' ? {
  gridArea: 'authorName',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  lineHeight: 1,
  color: (theme.vars || theme).palette.primary.main
} : _extends({}, ownerState?.isOwnMessage ? {
  textAlign: 'right',
  paddingInlineEnd: `calc(var(--MuiChatMessage-avatarSize) + ${theme.spacing(2)} + ${theme.spacing(0.5)})`
} : {
  paddingInlineStart: `calc(var(--MuiChatMessage-avatarSize) + ${theme.spacing(2)} + ${theme.spacing(0.5)})`
})));
function HiddenAuthorName() {
  return null;
}
const ChatMessageGroupTimestampStyled = styled('span', {
  name: 'MuiChatMessage',
  slot: 'GroupTimestamp'
})(({
  theme
}) => ({
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightRegular,
  color: (theme.vars || theme).palette.text.disabled,
  whiteSpace: 'nowrap',
  flexShrink: 0
}));
const ChatMessageGroup = /*#__PURE__*/React.forwardRef(function ChatMessageGroup(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatMessageGroup'
  });
  const _ref = props,
    {
      slots,
      slotProps,
      className,
      sx,
      children,
      messageId,
      classes: classesProp
    } = _ref,
    other = _objectWithoutPropertiesLoose(_ref, _excluded);
  void classesProp;
  const classes = useChatMessageUtilityClasses(undefined);

  // `slotProps.messageGroup` is typed `Partial<ChatMessageGroupProps>`, so it can
  // carry MessageGroup-primitive props (notably `groupKey` for time-window
  // grouping) alongside wrapper styling. Route the primitives to the headless
  // `<MessageGroup>` and keep only wrapper props (className/sx/DOM) on the group
  // slot — otherwise `groupKey` is ignored and these non-DOM props leak onto the
  // wrapper element. Row-structural values (index/items/messageId) from the props
  // win, so the slotProps copies act only as a fallback.
  const _ref2 = slotProps?.messageGroup ?? {},
    {
      groupKey: messageGroupGroupKey,
      index: messageGroupIndex,
      items: messageGroupItems,
      messageId: messageGroupMessageIdProp
    } = _ref2,
    messageGroupWrapperProps = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  void messageGroupMessageIdProp;

  // Map the flat `message*` keys onto the inner `ChatMessage`'s short local
  // slots. `messageRoot` swaps ChatMessage's own styled root — it is applied by
  // ChatMessage (not hoisted to the row component), so a raw element (e.g.
  // `'section'`) never receives `messageId`/`slots` it can't read.
  const innerMessageSlots = {
    root: slots?.messageRoot,
    avatar: slots?.messageAvatar,
    content: slots?.messageContent,
    meta: slots?.messageMeta,
    inlineMeta: slots?.messageInlineMeta,
    error: slots?.messageError,
    actions: slots?.messageActions,
    authorName: slots?.messageAuthorName,
    streamingIndicator: slots?.streamingIndicator
  };
  const innerMessageSlotProps = {
    root: slotProps?.messageRoot,
    avatar: slotProps?.messageAvatar,
    content: slotProps?.messageContent,
    meta: slotProps?.messageMeta,
    inlineMeta: slotProps?.messageInlineMeta,
    error: slotProps?.messageError,
    actions: slotProps?.messageActions,
    authorName: slotProps?.messageAuthorName,
    streamingIndicator: slotProps?.streamingIndicator
  };

  // Track whether the avatar slot is explicitly nulled — used to mirror the
  // `noAvatar` class on the group wrapper so any descendant CSS that reads
  // `var(--MuiChatMessage-avatarSize)` can collapse the reserved space.
  const hasAvatar = slots?.messageAvatar !== null;

  // `authorName` is rendered by the headless MessageGroup (group-level), but
  // consumer-facing it lives under the flat `messageAuthorName` key so all
  // per-row overrides cluster in one namespace. Null hides the label entirely.
  const authorNameOverride = slots?.messageAuthorName;
  const AuthorNameSlot = authorNameOverride === null ? HiddenAuthorName : authorNameOverride ?? ChatMessageGroupAuthorNameStyled;

  // Render priority:
  // 1. Explicit `children` (legacy: caller provided their own composition)
  // 2. Inner ChatMessage instance with the mapped short slot map.
  let resolvedChildren = children;
  if (children === undefined) {
    resolvedChildren = messageId ? /*#__PURE__*/_jsx(ChatMessage, {
      messageId: messageId,
      slots: innerMessageSlots,
      slotProps: innerMessageSlotProps
    }) : null;
  }
  return /*#__PURE__*/_jsx(MessageGroup, _extends({
    ref: ref,
    messageId: messageId
  }, messageGroupGroupKey !== undefined ? {
    groupKey: messageGroupGroupKey
  } : {}, messageGroupIndex !== undefined ? {
    index: messageGroupIndex
  } : {}, messageGroupItems !== undefined ? {
    items: messageGroupItems
  } : {}, other, {
    slots: {
      group: slots?.messageGroup ?? ChatMessageGroupStyled,
      authorName: AuthorNameSlot,
      groupTimestamp: ChatMessageGroupTimestampStyled
    },
    slotProps: {
      group: mergeSlotProps({
        className: clsx(classes.group, className, !hasAvatar && classes.noAvatar),
        sx
      }, messageGroupWrapperProps),
      authorName: slotProps?.messageAuthorName
    },
    children: resolvedChildren
  }));
});
if (process.env.NODE_ENV !== "production") ChatMessageGroup.displayName = "ChatMessageGroup";
process.env.NODE_ENV !== "production" ? ChatMessageGroup.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  children: PropTypes.node,
  className: PropTypes.string,
  /**
   * A function that maps a message to a group key.
   * Messages that resolve to the same key are visually grouped (shared avatar, author name, etc.).
   * Use `createTimeWindowGroupKey(windowMs)` to replicate time-window-based grouping.
   * @default (message) => message.author?.id ?? message.role ?? ''
   */
  groupKey: PropTypes.func,
  index: PropTypes.number,
  items: PropTypes.arrayOf(PropTypes.string),
  messageId: PropTypes.string.isRequired,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatMessageGroup };